<template>
  <el-dialog
    :title="dialogTitle"
    :visible.sync="dialogVisible"
    width="650px"
    :close-on-click-modal="false"
    @close="handleClose"
  >
    <el-form
      ref="bannerForm"
      :model="formData"
      :rules="formRules"
      label-width="120px"
    >
      <el-form-item label="图片名称" prop="title">
        <el-input v-model="formData.title" placeholder="请输入图片名称" />
      </el-form-item>
      
      <el-form-item label="图片上传" prop="image_url">
        <el-upload
          class="banner-uploader"
          :action="uploadAction"
          :headers="uploadHeaders"
          :show-file-list="false"
          :on-success="handleImageSuccess"
          :before-upload="beforeImageUpload"
          accept="image/*"
        >
          <img v-if="formData.image_url" :src="formData.image_url" class="banner-image" />
          <i v-else class="el-icon-plus banner-uploader-icon"></i>
        </el-upload>
        <div class="upload-tip">只能上传jpg/png文件，且不超过2MB</div>
      </el-form-item>
      
      <el-form-item label="跳转链接类型" prop="target_url_type">
        <el-select v-model="formData.target_url_type" placeholder="请选择跳转链接类型" @change="handleUrlTypeChange">
          <el-option label="跳转链接" :value="2" />
        </el-select>
      </el-form-item>
      
      <el-form-item 
        v-if="formData.target_url_type === 2" 
        label="跳转链接" 
        prop="target_url"
      >
        <el-input v-model="formData.target_url" placeholder="请输入跳转链接" />
      </el-form-item>
      
      <el-form-item label="平台类型" prop="show_type">
        <el-radio-group v-model="formData.show_type">
          <el-radio :label="1">PC</el-radio>
          <el-radio :label="2">H5</el-radio>
          <el-radio :label="4">业务员</el-radio>
          <el-radio :label="8">H5引导图片</el-radio>
        </el-radio-group>
      </el-form-item>
      
      <el-form-item label="排序" prop="sort">
        <el-input-number v-model="formData.sort" :min="0" :max="999" />
      </el-form-item>
      
      <el-form-item label="状态" prop="disabled">
        <el-radio-group v-model="formData.disabled">
          <el-radio :label="false">启用</el-radio>
          <el-radio :label="true">禁用</el-radio>
        </el-radio-group>
      </el-form-item>
    </el-form>
    
    <div slot="footer" class="dialog-footer">
      <el-button @click="handleClose">取消</el-button>
      <el-button type="primary" :loading="submitLoading" @click="handleSubmit">
        确定
      </el-button>
    </div>
  </el-dialog>
</template>

<script>
import { saveBanner } from '@/api/custom-frontend'

export default {
  name: 'BannerEditDialog',
  props: {
    visible: {
      type: Boolean,
      default: false
    },
    bannerData: {
      type: Object,
      default: null
    },
    appInfo: {
      type: Object,
      required: true
    }
  },
  data() {
    return {
      submitLoading: false,
      formData: {
        title: '',
        image_url: '',
        target_url: '',
        target_url_type: 2, // 默认为跳转链接
        show_type: 1,
        sort: 0,
        disabled: false
      },
      formRules: {
        title: [
          { required: true, message: '请输入图片名称', trigger: 'blur' }
        ],
        image_url: [
          { required: true, message: '请上传图片', trigger: 'change' }
        ],
        target_url: [
          { 
            required: true, 
            message: '请输入跳转链接', 
            trigger: 'blur',
            validator: (rule, value, callback) => {
              if (this.formData.target_url_type === 2 && !value) {
                callback(new Error('请输入跳转链接'))
              } else {
                callback()
              }
            }
          }
        ],
        target_url_type: [
          { required: true, message: '请选择跳转链接类型', trigger: 'change' }
        ],
        show_type: [
          { required: true, message: '请选择平台类型', trigger: 'change' }
        ]
      }
    }
  },
  computed: {
    dialogVisible: {
      get() {
        return this.visible
      },
      set(val) {
        this.$emit('update:visible', val)
      }
    },
    
    dialogTitle() {
      return this.bannerData ? '编辑Banner图' : '添加Banner图'
    },
    
    uploadAction() {
      // 使用与老项目一致的上传接口地址
      return process.env.VUE_APP_BASE_URL + '/res/file/upload'
    },
    
    uploadHeaders() {
      return {
        'Authorization': 'Bearer ' + this.$store.getters.token
      }
    }
  },
  watch: {
    visible(val) {
      if (val) {
        this.initFormData()
      }
    }
  },
  methods: {
    initFormData() {
      if (this.bannerData) {
        this.formData = {
          id: this.bannerData.id,
          title: this.bannerData.title,
          image_url: this.bannerData.image_url,
          target_url: this.bannerData.target_url,
          target_url_type: this.bannerData.target_url_type || 2,
          show_type: this.bannerData.show_type,
          sort: this.bannerData.sort,
          disabled: this.bannerData.disabled
        }
      } else {
        this.formData = {
          title: '',
          image_url: '',
          target_url: '',
          target_url_type: 2,
          show_type: 1,
          sort: 0,
          disabled: false
        }
      }
    },
    
    handleImageSuccess(res, file) {
      if (res.err_code === 0) {
        // 与老项目保持一致，直接使用 res.data
        this.formData.image_url = res.data
        this.$message.success('图片上传成功')
      } else {
        this.$message.error(res.err_msg || '图片上传失败')
      }
    },
    
    beforeImageUpload(file) {
      const isJPG = file.type === 'image/jpeg' || file.type === 'image/png'
      const isLt2M = file.size / 1024 / 1024 < 2

      if (!isJPG) {
        this.$message.error('上传图片只能是 JPG/PNG 格式!')
      }
      if (!isLt2M) {
        this.$message.error('上传图片大小不能超过 2MB!')
      }
      return isJPG && isLt2M
    },
    
    handleUrlTypeChange(value) {
      // 当跳转链接类型变化时，清空跳转链接
      if (value !== 2) {
        this.formData.target_url = ''
      }
    },
    
    async handleSubmit() {
      try {
        await this.$refs.bannerForm.validate()
        
        this.submitLoading = true
        const submitData = {
          ...this.formData,
          app_id: this.appInfo.id
        }
        
        const res = await saveBanner(submitData)
        if (res && res.err_code === 0) {
          this.$message.success('保存成功')
          this.$emit('refresh')
          this.handleClose()
        } else {
          this.$message.error(res.err_msg || '保存失败')
        }
      } catch (error) {
        console.error('保存Banner失败:', error)
        this.$message.error('保存失败')
      } finally {
        this.submitLoading = false
      }
    },
    
    handleClose() {
      this.dialogVisible = false
      this.$refs.bannerForm.resetFields()
    }
  }
}
</script>

<style lang="less" scoped>
.banner-uploader {
  .banner-image {
    width: 150px;
    height: 150px;
    display: block;
    object-fit: cover;
    border: 1px solid #d9d9d9;
    border-radius: 6px;
  }
}

/deep/ .banner-uploader .el-upload {
  border: 1px dashed #d9d9d9;
  border-radius: 6px;
  cursor: pointer;
  position: relative;
  overflow: hidden;
  width: 150px;
  height: 150px;
  
  &:hover {
    border-color: #409EFF;
  }
}

.banner-uploader-icon {
  font-size: 28px;
  color: #8c939d;
  width: 150px;
  height: 150px;
  line-height: 150px;
  text-align: center;
}

.upload-tip {
  font-size: 12px;
  color: #999;
  margin-top: 5px;
}
</style>